import Matter from "matter-js";
import { Application, Container, type FederatedPointerEvent, Graphics, Text } from "pixi.js";
import { type GhostState, clearGhosts, createGhostState, tickGhosts } from "./ghost";
import { loadLevel } from "./level";
import { ORGAN_INFO } from "./organ-info";
import { ORGAN_GEOMETRY, Organ, SPEISEROEHRE_SPEED, preloadOrganTextures } from "./organs";
import { Particle } from "./particles";
import { createPhysics } from "./physics";
import { PARTICLE_COLORS, PARTICLE_LABELS, transform } from "./rules";
import type { Level, OrganKind, ParticleType, TutorialAnchor, TutorialStep } from "./types";

function showFatalError(msg: string): void {
  const el = document.createElement("pre");
  el.style.cssText =
    "color:#b00;padding:20px;font-family:monospace;font-size:13px;white-space:pre-wrap;background:#fff;margin:0;position:fixed;top:0;left:0;right:0;bottom:0;z-index:99999;overflow:auto;";
  el.textContent = `[incredible-intestine fatal]\n${msg}`;
  document.body.replaceChildren(el);
}
window.addEventListener("error", (e) => {
  showFatalError(`${e.message}\n  at ${e.filename}:${e.lineno}:${e.colno}\n${e.error?.stack ?? ""}`);
});
window.addEventListener("unhandledrejection", (e) => {
  const reason = e.reason;
  const text =
    reason instanceof Error ? `${reason.message}\n${reason.stack ?? ""}` : String(reason);
  showFatalError(`UnhandledRejection: ${text}`);
});

// ─── Responsive Layout ────────────────────────────────────────────────────────
interface Layout {
  PALETTE_W: number;    // left panel width (0 on mobile)
  GOALS_W: number;      // right panel width (0 on mobile)
  HEADER_H: number;     // top bar height
  BOTTOM_H: number;     // bottom palette bar height (0 on desktop)
  SLOT_W: number;       // palette slot card width
  SLOT_H: number;       // palette slot card height
  HANDLE_R: number;     // rotation handle hit-circle radius
  isMobile: boolean;
  organScale: number;   // scale applied to organ sprites + physics bodies
  particleR: number;    // particle radius in px
}

function computeLayout(): Layout {
  const isMobile = window.innerWidth < 640;
  if (isMobile) {
    return {
      PALETTE_W: 0, GOALS_W: 0, HEADER_H: 46, BOTTOM_H: 88,
      SLOT_W: 72, SLOT_H: 72, HANDLE_R: 22, isMobile: true,
      organScale: 0.82, particleR: 9,
    };
  }
  return {
    PALETTE_W: 170, GOALS_W: 220, HEADER_H: 88, BOTTOM_H: 0,
    SLOT_W: 150, SLOT_H: 100, HANDLE_R: 18, isMobile: false,
    organScale: 1.0, particleR: 11,
  };
}

let layout = computeLayout();

// Trash zone dimensions
const TRASH_W = 100;
const TRASH_H = 76;
const TRASH_MARGIN = 14;

// F32: Deep-Link from info popup into "Verdauung Steuern" prototype.
const VS_DEEPLINK_BASE = "https://fokusgruppe.playful-mintful.ch/games/verdauung-steuern/";
const VS_HASH_BY_ORGAN: Partial<Record<OrganKind, string>> = {
  mund: "mund",
  speiseroehre: "speise",
  magen: "magen",
  leber: "leber",
  pankreas: "bauch",
  darm: "duenn",
};

const app = new Application();
await app.init({
  resizeTo: window,
  background: "#fdf6e3",
  antialias: true,
  resolution: Math.min(window.devicePixelRatio, 2),
  autoDensity: true,
});
document.body.appendChild(app.canvas);

await preloadOrganTextures();

const phys = createPhysics();

// Layers
const bgLayer = new Container();
const workbenchLayer = new Container();
const organLayer = new Container();
const particleLayer = new Container();
const uiLayer = new Container();
app.stage.addChild(bgLayer, workbenchLayer, organLayer, particleLayer, uiLayer);

// Workbench area (center) — reactive to layout
const workbench = {
  get x() { return layout.PALETTE_W; },
  get y() { return layout.HEADER_H; },
  get w() { return app.screen.width - layout.PALETTE_W - layout.GOALS_W; },
  get h() { return app.screen.height - layout.HEADER_H - layout.BOTTOM_H; },
};

// Outer screen walls (rebuilt on resize to match new screen dimensions)
const outerWalls: Matter.Body[] = [];
function rebuildOuterWalls(): void {
  for (const b of outerWalls) Matter.World.remove(phys.world, b);
  outerWalls.length = 0;
  const opts: Matter.IBodyDefinition = { isStatic: true, friction: 0.2, restitution: 0.1, label: "wall" };
  const w = app.screen.width;
  const h = app.screen.height;
  const T = 60;
  const walls = [
    Matter.Bodies.rectangle(w / 2, -T / 2, w, T, opts),
    Matter.Bodies.rectangle(-T / 2, h / 2, T, h * 2, opts),
    Matter.Bodies.rectangle(w + T / 2, h / 2, T, h * 2, opts),
  ];
  for (const b of walls) Matter.World.add(phys.world, b);
  outerWalls.push(...walls);
}
rebuildOuterWalls();

// Static walls around workbench
const sideWalls: Matter.Body[] = [];
function rebuildBoundary(): void {
  for (const b of sideWalls) Matter.World.remove(phys.world, b);
  sideWalls.length = 0;
  const opts: Matter.IBodyDefinition = { isStatic: true, friction: 0.1, restitution: 0.1, label: "wall" };
  const walls = [
    Matter.Bodies.rectangle(workbench.x, workbench.y + workbench.h / 2, 20, workbench.h, opts),
    Matter.Bodies.rectangle(workbench.x + workbench.w, workbench.y + workbench.h / 2, 20, workbench.h, opts),
    Matter.Bodies.rectangle(workbench.x + workbench.w / 2, workbench.y, workbench.w, 20, opts),
  ];
  for (const w of walls) Matter.World.add(phys.world, w);
  sideWalls.push(...walls);
}

// State
type Mode = "building" | "running" | "won";
const state = {
  mode: "building" as Mode,
  level: null as Level | null,
  levels: [
    `${import.meta.env.BASE_URL}levels/level-0.json`,
    `${import.meta.env.BASE_URL}levels/level-1.json`,
    `${import.meta.env.BASE_URL}levels/level-2.json`,
    `${import.meta.env.BASE_URL}levels/level-3.json`,
  ],
  levelIdx: 0,
  paletteStock: new Map<OrganKind, number>(),
  organs: [] as Organ[],
  particles: [] as Particle[],
  progress: new Map<ParticleType, number>(),
  spawnTimer: 0,
  spawnQueue: [] as ParticleType[],
  ghosts: createGhostState(0) as GhostState,
};

// --- Rendering helpers ---

function drawBackground(): void {
  bgLayer.removeChildren();
  const panels = new Graphics();
  if (!layout.isMobile) {
    panels.rect(0, 0, layout.PALETTE_W, app.screen.height).fill({ color: 0xf1e7cf });
    panels.rect(app.screen.width - layout.GOALS_W, 0, layout.GOALS_W, app.screen.height).fill({ color: 0xf1e7cf });
  }
  panels.rect(0, 0, app.screen.width, layout.HEADER_H).fill({ color: 0xeadcb4 });
  if (layout.isMobile && layout.BOTTOM_H > 0) {
    panels.rect(0, app.screen.height - layout.BOTTOM_H, app.screen.width, layout.BOTTOM_H).fill({ color: 0xf1e7cf });
  }
  panels.stroke({ color: 0xc9b98a, width: 2 });
  bgLayer.addChild(panels);

  // Workbench grid
  const grid = new Graphics();
  const step = 40;
  grid.stroke({ color: 0xe8dcc0, width: 1 });
  for (let x = workbench.x; x < workbench.x + workbench.w; x += step) {
    grid.moveTo(x, workbench.y).lineTo(x, workbench.y + workbench.h);
  }
  for (let y = workbench.y; y < workbench.y + workbench.h; y += step) {
    grid.moveTo(workbench.x, y).lineTo(workbench.x + workbench.w, y);
  }
  bgLayer.addChild(grid);
}

// --- UI panels ---

let titleText: Text;
let hintText: Text | null = null;
let runBtn: Container;
let nextBtn: Container;
let tiltBtn: Container;
let goalChips: Container | null = null;   // mobile: inline goal chips in header
let feedbackOverlay: Container | null = null;

function makeButton(label: string, w: number, h: number, onTap: () => void): Container {
  const c = new Container();
  const bg = new Graphics()
    .roundRect(0, 0, w, h, 10)
    .fill({ color: 0x3a2a1a })
    .stroke({ color: 0x1e1410, width: 2 });
  const t = new Text({
    text: label,
    style: { fontFamily: "Georgia", fontSize: layout.isMobile ? 14 : 16, fill: 0xfdf6e3, fontWeight: "bold" },
  });
  t.anchor.set(0.5);
  t.position.set(w / 2, h / 2);
  c.addChild(bg, t);
  c.eventMode = "static";
  c.cursor = "pointer";
  c.on("pointertap", onTap);
  (c as Container & { setLabel(s: string): void }).setLabel = (s: string) => { t.text = s; };
  return c;
}

function drawGoalChipsMobile(): void {
  if (!layout.isMobile || !state.level) return;
  if (goalChips) { uiLayer.removeChild(goalChips); goalChips.destroy({ children: true }); goalChips = null; }

  const c = new Container();
  let cx = layout.PALETTE_W + (titleText ? titleText.width + 28 : 8);
  const cy = layout.HEADER_H / 2;

  for (const goal of state.level.goals) {
    const have = state.progress.get(goal.type) ?? 0;
    const done = have >= goal.count;
    const chip = new Container();
    const chipBg = new Graphics()
      .roundRect(0, -11, 52, 22, 6)
      .fill({ color: done ? 0x4caf50 : PARTICLE_COLORS[goal.type], alpha: 0.85 });
    const chipT = new Text({
      text: `${PARTICLE_LABELS[goal.type]} ${have}/${goal.count}`,
      style: { fontFamily: "Georgia", fontSize: 11, fill: 0x3a2a1a, fontWeight: "bold" },
    });
    chipT.anchor.set(0, 0.5);
    chipT.position.set(4, 0);
    chip.addChild(chipBg, chipT);
    chip.position.set(cx, cy);
    c.addChild(chip);
    cx += 58;
    if (cx + 58 > app.screen.width - 90) break; // leave room for run button
  }
  uiLayer.addChild(c);
  goalChips = c;
}

function drawHeader(): void {
  if (!titleText) {
    titleText = new Text({
      text: "",
      style: { fontFamily: "Georgia", fontSize: layout.isMobile ? 15 : 22, fill: 0x3a2a1a, fontWeight: "bold" },
    });
    if (!layout.isMobile) {
      hintText = new Text({
        text: "",
        style: { fontFamily: "Georgia", fontSize: 13, fill: 0x5a4a2a, wordWrap: true, wordWrapWidth: 520 },
      });
    }
    const btnH = layout.isMobile ? 36 : 40;
    const btnW = layout.isMobile ? 100 : 120;
    runBtn = makeButton("▶ Start", btnW, btnH, onToggleRun);
    nextBtn = makeButton("▶| Nächstes Level", layout.isMobile ? 160 : 200, btnH, onNextLevel);
    tiltBtn = makeButton("🤲 Schütteln", layout.isMobile ? 120 : 140, btnH, onTilt);
    const uiItems: Container[] = [titleText, runBtn, nextBtn, tiltBtn];
    if (hintText) uiItems.push(hintText);
    for (const item of uiItems) uiLayer.addChild(item);
    setupSkipHotspot();
  }

  // Re-apply font size if layout changed
  (titleText as Text & { style: { fontSize: number } }).style.fontSize = layout.isMobile ? 15 : 22;

  titleText.position.set(layout.PALETTE_W + (layout.isMobile ? 8 : 20), layout.isMobile ? 10 : 14);
  if (hintText) hintText.position.set(layout.PALETTE_W + 20, 46);
  repositionSkipHotspot();

  const label = state.mode === "running" ? "⏹ Stop" : "▶ Start";
  (runBtn as Container & { setLabel(s: string): void }).setLabel(label);

  const btnH = layout.isMobile ? 36 : 40;
  const btnMargin = layout.isMobile ? 6 : 24;

  if (state.mode === "won") {
    const replayW = layout.isMobile ? 100 : 170;
    const nextW = layout.isMobile ? 160 : 220;
    runBtn.position.set(app.screen.width - layout.GOALS_W - replayW - nextW - btnMargin * 2, (layout.HEADER_H - btnH) / 2);
    nextBtn.position.set(app.screen.width - layout.GOALS_W - nextW - btnMargin, (layout.HEADER_H - btnH) / 2);
    runBtn.visible = true; nextBtn.visible = true; tiltBtn.visible = false;
  } else if (state.mode === "running") {
    const runW = layout.isMobile ? 100 : 120;
    const tiltW = layout.isMobile ? 120 : 140;
    runBtn.position.set(app.screen.width - layout.GOALS_W - runW - tiltW - btnMargin * 2, (layout.HEADER_H - btnH) / 2);
    tiltBtn.position.set(app.screen.width - layout.GOALS_W - tiltW - btnMargin, (layout.HEADER_H - btnH) / 2);
    runBtn.visible = true; nextBtn.visible = false; tiltBtn.visible = true;
  } else {
    const runW = layout.isMobile ? 100 : 120;
    runBtn.position.set(app.screen.width - layout.GOALS_W - runW - btnMargin, (layout.HEADER_H - btnH) / 2);
    runBtn.visible = true; nextBtn.visible = false; tiltBtn.visible = false;
  }

  // Mobile: show goal chips inline in header
  if (layout.isMobile) drawGoalChipsMobile();
}

// --- Tilt ---

let tiltShakeUntil = 0;
let tiltShakeMag = 0;
function onTilt(): void {
  if (state.mode !== "running") return;
  tiltShakeUntil = performance.now() + 600;
  tiltShakeMag = 12;
  for (const p of state.particles) {
    Matter.Body.setVelocity(p.body, {
      x: p.body.velocity.x + (Math.random() - 0.5) * 6,
      y: p.body.velocity.y - Math.random() * 5 - 1,
    });
  }
}
function updateGoalPulses(now: number): void {
  for (const [type, row] of goalRows) {
    const until = goalPulseUntil.get(type) ?? 0;
    const remaining = until - now;
    if (remaining <= 0) {
      row.scale.set(1);
      if (until > 0) goalPulseUntil.delete(type);
      continue;
    }
    const t = 1 - remaining / GOAL_PULSE_MS;
    const bump = Math.sin(t * Math.PI) * 0.18;
    row.scale.set(1 + bump);
  }
}

function applyShake(now: number): void {
  if (now < tiltShakeUntil) {
    const t = (tiltShakeUntil - now) / 600;
    const mag = tiltShakeMag * t;
    app.stage.position.set((Math.random() - 0.5) * mag, (Math.random() - 0.5) * mag);
  } else if (app.stage.position.x !== 0 || app.stage.position.y !== 0) {
    app.stage.position.set(0, 0);
  }
}

// Hidden skip-level hotspot (3 taps top-right within 1.5s)
let skipHotspot: Container | null = null;
let skipTaps: number[] = [];
function setupSkipHotspot(): void {
  const hot = new Container();
  const hit = new Graphics().rect(0, 0, 64, 64).fill({ color: 0xffffff, alpha: 0.001 });
  hot.addChild(hit);
  hot.eventMode = "static";
  hot.cursor = "default";
  hot.on("pointertap", () => {
    const now = performance.now();
    skipTaps = skipTaps.filter((t) => now - t < 1500);
    skipTaps.push(now);
    if (skipTaps.length >= 3) { skipTaps = []; onNextLevel(); }
  });
  uiLayer.addChild(hot);
  skipHotspot = hot;
}
function repositionSkipHotspot(): void {
  if (skipHotspot) skipHotspot.position.set(app.screen.width - 64, 0);
}

// Palette
const paletteContainer = new Container();
uiLayer.addChild(paletteContainer);

let trashState: "idle" | "armed" | "hot" = "idle";
let trashContainer: Container | null = null;

function redrawTrash(): void {
  if (!trashContainer) return;
  trashContainer.removeChildren();
  const colors = {
    idle: { fill: 0xeadcb4, stroke: 0x7a6a4a, iconScale: 1 },
    armed: { fill: 0xf4d4c4, stroke: 0xc04030, iconScale: 1 },
    hot: { fill: 0xf8b8a4, stroke: 0xc04030, iconScale: 1.15 },
  }[trashState];
  const bg = new Graphics()
    .roundRect(0, 0, TRASH_W, TRASH_H, 10)
    .fill({ color: colors.fill })
    .stroke({ color: colors.stroke, width: 2 });
  const icon = new Text({ text: "🗑", style: { fontSize: 26 * colors.iconScale, fill: 0x3a2a1a } });
  icon.anchor.set(0.5);
  icon.position.set(TRASH_W / 2, TRASH_H / 2 - 6);
  const label = new Text({ text: "Abfall", style: { fontFamily: "Georgia", fontSize: 11, fill: 0x3a2a1a, fontWeight: "bold" } });
  label.anchor.set(0.5, 0);
  label.position.set(TRASH_W / 2, TRASH_H - 16);
  trashContainer.addChild(bg, icon, label);
}

function getTrashBounds(): { x: number; y: number; w: number; h: number } {
  if (layout.isMobile) {
    // Trash lives at left of bottom bar on mobile
    return {
      x: TRASH_MARGIN,
      y: app.screen.height - layout.BOTTOM_H + (layout.BOTTOM_H - TRASH_H) / 2,
      w: TRASH_W,
      h: TRASH_H,
    };
  }
  return {
    x: (layout.PALETTE_W - TRASH_W) / 2,
    y: app.screen.height - TRASH_H - TRASH_MARGIN,
    w: TRASH_W,
    h: TRASH_H,
  };
}

function isOverTrash(gx: number, gy: number): boolean {
  if (!trashContainer) return false;
  const b = getTrashBounds();
  return gx >= b.x && gx <= b.x + b.w && gy >= b.y && gy <= b.y + b.h;
}

function setTrashState(next: "idle" | "armed" | "hot"): void {
  if (next === trashState) return;
  trashState = next;
  if (trashContainer) trashContainer.visible = trashState !== "idle";
  redrawTrash();
}

// --- Tutorial Speech Bubbles ---
let tutorialBubble: Container | null = null;
let tutorialQueue: TutorialStep[] = [];
let tutorialIdx = 0;
let tutorialLevelId: string | null = null;

function tutorialStorageKey(levelId: string): string { return `intestine.tutorial.${levelId}.seen`; }
function isTutorialSeen(levelId: string): boolean {
  try { return localStorage.getItem(tutorialStorageKey(levelId)) === "1"; } catch { return false; }
}
function markTutorialSeen(levelId: string): void {
  try { localStorage.setItem(tutorialStorageKey(levelId), "1"); } catch {}
}
function closeTutorialBubble(): void {
  if (!tutorialBubble) return;
  uiLayer.removeChild(tutorialBubble);
  tutorialBubble.destroy({ children: true });
  tutorialBubble = null;
  tutorialQueue = []; tutorialIdx = 0; tutorialLevelId = null;
}

function resolveAnchor(anchor: TutorialAnchor | undefined): { x: number; y: number } | null {
  if (!anchor) return null;
  if (typeof anchor === "object" && "placed" in anchor) {
    const organ = state.organs.find((o) => o.kind === anchor.placed);
    if (!organ) return null;
    return { x: organ.view.x, y: organ.view.y };
  }
  switch (anchor) {
    case "hopper": {
      if (spawners.length === 0) return null;
      const s = spawners[0];
      return { x: s.x, y: s.y };
    }
    case "run-button":
      return { x: app.screen.width - layout.GOALS_W - (layout.isMobile ? 50 : 70), y: layout.HEADER_H / 2 };
    case "rezept":
      return layout.isMobile
        ? { x: app.screen.width / 2, y: workbench.y + 60 }
        : { x: app.screen.width - layout.GOALS_W + 40, y: layout.HEADER_H + 240 };
    case "palette":
      return layout.isMobile
        ? { x: app.screen.width / 2, y: app.screen.height - layout.BOTTOM_H / 2 }
        : { x: layout.PALETTE_W / 2, y: layout.HEADER_H + 100 };
    case "trash":
      return layout.isMobile
        ? { x: TRASH_MARGIN + TRASH_W / 2, y: app.screen.height - layout.BOTTOM_H / 2 }
        : { x: layout.PALETTE_W / 2, y: app.screen.height - TRASH_H - TRASH_MARGIN + TRASH_H / 2 };
    default:
      return null;
  }
}

function renderTutorialBubble(): void {
  if (tutorialBubble) { uiLayer.removeChild(tutorialBubble); tutorialBubble.destroy({ children: true }); tutorialBubble = null; }
  if (tutorialIdx >= tutorialQueue.length) return;
  const step = tutorialQueue[tutorialIdx];
  const BUBBLE_W = layout.isMobile ? Math.min(280, app.screen.width - 20) : 320;
  const pad = 14;

  const body = new Text({
    text: step.text,
    style: { fontFamily: "Georgia", fontSize: 14, fill: 0x3a2a1a, wordWrap: true, wordWrapWidth: BUBBLE_W - pad * 2 },
  });
  body.position.set(pad, pad);
  const progress = new Text({
    text: `${tutorialIdx + 1} / ${tutorialQueue.length}`,
    style: { fontFamily: "Georgia", fontSize: 11, fill: 0x7a6a4a, fontStyle: "italic" },
  });
  const bodyH = Math.max(40, body.height);
  const height = pad + bodyH + 8 + 32 + pad;
  progress.position.set(pad, height - pad - 26);

  const isLast = tutorialIdx === tutorialQueue.length - 1;
  const btnLabel = isLast ? "Verstanden ▶" : "Weiter ▶";
  const nextBtnInner = makeButton(btnLabel, 130, 30, () => {
    tutorialIdx++;
    if (tutorialIdx >= tutorialQueue.length) {
      if (tutorialLevelId) markTutorialSeen(tutorialLevelId);
      closeTutorialBubble();
    } else { renderTutorialBubble(); }
  });
  nextBtnInner.position.set(BUBBLE_W - 130 - pad, height - pad - 30);
  const bg = new Graphics()
    .roundRect(0, 0, BUBBLE_W, height, 14)
    .fill({ color: 0xfdf6e3 })
    .stroke({ color: 0x3a2a1a, width: 3 });
  const c = new Container();
  c.addChild(bg, body, progress, nextBtnInner);

  if (tutorialIdx === 0 && tutorialQueue.length > 1) {
    const skip = new Container();
    const skipR = 14;
    const skipRing = new Graphics().circle(0, 0, skipR).fill({ color: 0xfdf6e3, alpha: 0 }).stroke({ color: 0x3a2a1a, width: 2 });
    const skipX = new Text({ text: "✕", style: { fontFamily: "Georgia", fontSize: 14, fill: 0x3a2a1a, fontWeight: "bold" } });
    skipX.anchor.set(0.5);
    skip.addChild(skipRing, skipX);
    skip.position.set(nextBtnInner.x - skipR - 10, nextBtnInner.y + 15);
    skip.eventMode = "static"; skip.cursor = "pointer";
    const skipHit = new Graphics().circle(0, 0, skipR).fill({ color: 0xffffff, alpha: 0.001 });
    skip.addChildAt(skipHit, 0);
    skip.on("pointertap", () => { if (tutorialLevelId) markTutorialSeen(tutorialLevelId); closeTutorialBubble(); });
    c.addChild(skip);
  }

  const anchor = resolveAnchor(step.anchor);
  let px: number; let py: number;
  if (anchor) {
    px = anchor.x + 40;
    py = anchor.y - height / 2;
    if (px + BUBBLE_W > app.screen.width - 8) px = anchor.x - BUBBLE_W - 40;
    if (py < layout.HEADER_H + 8) py = layout.HEADER_H + 8;
    if (py + height > app.screen.height - layout.BOTTOM_H - 8) py = app.screen.height - layout.BOTTOM_H - height - 8;
    const tri = new Graphics();
    const bubbleCx = px + BUBBLE_W / 2;
    const bubbleCy = py + height / 2;
    const toRight = anchor.x > bubbleCx;
    const edgeX = toRight ? px + BUBBLE_W : px;
    const edgeY = Math.max(py + 20, Math.min(py + height - 20, anchor.y));
    tri.moveTo(edgeX - px, edgeY - py)
      .lineTo(anchor.x - px, anchor.y - py)
      .lineTo(edgeX - px, edgeY - py + (toRight ? 16 : -16))
      .closePath()
      .fill({ color: 0xfdf6e3 })
      .stroke({ color: 0x3a2a1a, width: 2 });
    c.addChildAt(tri, 0);
    void bubbleCy;
  } else {
    px = app.screen.width / 2 - BUBBLE_W / 2;
    py = layout.HEADER_H + 16;
  }
  c.position.set(px, py);
  uiLayer.addChild(c);
  tutorialBubble = c;
}

function showTutorialBubbles(levelId: string, steps: (string | TutorialStep)[]): void {
  closeTutorialBubble();
  if (isTutorialSeen(levelId)) return;
  tutorialLevelId = levelId;
  tutorialQueue = steps.map((s) => (typeof s === "string" ? { text: s } : s));
  tutorialIdx = 0;
  renderTutorialBubble();
}

// --- Organ-Info Popup ---
let infoPopup: Container | null = null;
let infoPopupKind: OrganKind | null = null;
let infoPopupVia: "hover" | "tap" = "hover";

function closeInfoPopup(): void {
  if (!infoPopup) return;
  uiLayer.removeChild(infoPopup);
  infoPopup.destroy({ children: true });
  infoPopup = null; infoPopupKind = null;
}

function openInfoPopup(kind: OrganKind, anchorX: number, anchorY: number, via: "hover" | "tap"): void {
  if (infoPopup && infoPopupKind === kind) {
    if (via === "tap") infoPopupVia = "tap";
    return;
  }
  closeInfoPopup();
  const info = ORGAN_INFO[kind];
  const POPUP_W = layout.isMobile ? 200 : 240;
  const pad = 10;
  const name = new Text({ text: info.name, style: { fontFamily: "Georgia", fontSize: 14, fill: 0xfdf6e3, fontWeight: "bold" } });
  name.position.set(pad, pad);
  const funktion = new Text({
    text: info.funktion,
    style: { fontFamily: "Georgia", fontSize: 12, fill: 0xfdf6e3, wordWrap: true, wordWrapWidth: POPUP_W - pad * 2 },
  });
  funktion.position.set(pad, pad + 22);
  let nextY = pad + 22 + funktion.height + 8;
  const regelTexts: Text[] = [];
  for (const r of info.regeln) {
    const t = new Text({ text: r, style: { fontFamily: "Georgia", fontSize: 12, fill: 0xfdf6e3, wordWrap: true, wordWrapWidth: POPUP_W - pad * 2 } });
    t.position.set(pad, nextY);
    regelTexts.push(t);
    nextY += Math.max(18, t.height + 2);
  }
  const height = nextY + pad;
  const bg = new Graphics().roundRect(0, 0, POPUP_W, height, 10).fill({ color: 0x3a2a1a }).stroke({ color: 0xfdf6e3, width: 2 });
  const c = new Container();
  c.addChild(bg, name, funktion, ...regelTexts);
  let px = anchorX + 12;
  if (px + POPUP_W > app.screen.width - 8) px = anchorX - POPUP_W - 12;
  let py = anchorY;
  if (py + height > app.screen.height - layout.BOTTOM_H - 8) py = app.screen.height - layout.BOTTOM_H - height - 8;
  if (py < layout.HEADER_H + 4) py = layout.HEADER_H + 4;
  c.position.set(px, py);
  c.eventMode = "static";
  c.on("pointertap", (e) => e.stopPropagation());
  uiLayer.addChild(c);
  infoPopup = c; infoPopupKind = kind; infoPopupVia = via;
}

app.stage.on("pointertap", () => {
  if (infoPopup && infoPopupVia === "tap") closeInfoPopup();
});

// ─── Palette ──────────────────────────────────────────────────────────────────

function drawPalette(): void {
  paletteContainer.removeChildren();
  if (layout.isMobile) {
    _drawPaletteMobile();
  } else {
    _drawPaletteDesktop();
  }
}

function _drawPaletteDesktop(): void {
  const title = new Text({ text: "Werkzeug", style: { fontFamily: "Georgia", fontSize: 16, fill: 0x3a2a1a, fontWeight: "bold" } });
  title.position.set(16, layout.HEADER_H + 12);
  paletteContainer.addChild(title);
  let y = layout.HEADER_H + 44;
  for (const [kind, remaining] of state.paletteStock.entries()) {
    const slot = buildPaletteSlot(kind, remaining);
    slot.position.set(10, y);
    paletteContainer.addChild(slot);
    y += 110;
  }
  // Trash zone at bottom of palette panel
  trashContainer = new Container();
  const b = getTrashBounds();
  trashContainer.position.set(b.x, b.y);
  trashContainer.visible = trashState !== "idle";
  redrawTrash();
  paletteContainer.addChild(trashContainer);
}

function _drawPaletteMobile(): void {
  const bottomY = app.screen.height - layout.BOTTOM_H;
  const slotGap = 8;
  const slotH = layout.SLOT_H;
  const padTop = (layout.BOTTOM_H - slotH) / 2;

  // Trash at left
  trashContainer = new Container();
  const b = getTrashBounds();
  trashContainer.position.set(b.x, b.y);
  trashContainer.visible = trashState !== "idle";
  redrawTrash();
  paletteContainer.addChild(trashContainer);

  // Organ slots, scrolling right from after trash
  let x = TRASH_MARGIN + TRASH_W + slotGap;
  for (const [kind, remaining] of state.paletteStock.entries()) {
    const slot = buildPaletteSlot(kind, remaining);
    slot.position.set(x, bottomY + padTop);
    paletteContainer.addChild(slot);
    x += layout.SLOT_W + slotGap;
  }
}

function buildPaletteSlot(kind: OrganKind, remaining: number): Container {
  const c = new Container();
  const geom = ORGAN_GEOMETRY[kind];
  const unlimited = kind === "ramp";
  const available = unlimited || remaining > 0;
  const sw = layout.SLOT_W;
  const sh = layout.SLOT_H;
  const bg = new Graphics()
    .roundRect(0, 0, sw, sh, 10)
    .fill({ color: available ? 0xfdf6e3 : 0xe2d4a8 })
    .stroke({ color: 0x3a2a1a, width: 2 });
  c.addChild(bg);

  // Mini preview — scaled to fit slot
  const previewScale = layout.isMobile ? 0.32 : 0.42;
  const preview = new Organ(kind, sw * 0.35, sh * 0.48);
  preview.view.scale.set(previewScale);
  c.addChild(preview.view);

  if (layout.isMobile) {
    // Compact mobile slot: label + count below icon
    const labelStr = geom.label.length > 8 ? geom.label.slice(0, 7) + "…" : geom.label;
    const label = new Text({ text: labelStr, style: { fontFamily: "Georgia", fontSize: 10, fill: 0x3a2a1a, fontWeight: "bold" } });
    label.anchor.set(0.5, 0);
    label.position.set(sw / 2, sh - 26);
    c.addChild(label);
    if (!unlimited) {
      const count = new Text({ text: `×${remaining}`, style: { fontFamily: "Georgia", fontSize: 11, fill: 0x3a2a1a, fontWeight: "bold" } });
      count.anchor.set(0.5, 0);
      count.position.set(sw / 2, sh - 14);
      c.addChild(count);
    }
  } else {
    // Desktop slot: label + count + hint + info icons
    const label = new Text({ text: `${geom.label}`, style: { fontFamily: "Georgia", fontSize: 14, fill: 0x3a2a1a, fontWeight: "bold" } });
    label.position.set(82, 18);
    c.addChild(label);
    if (!unlimited) {
      const count = new Text({ text: `×${remaining}`, style: { fontFamily: "Georgia", fontSize: 14, fill: 0x3a2a1a, fontWeight: "bold" } });
      count.position.set(82, 40);
      c.addChild(count);
    }
    const hint = new Text({ text: available ? "Ziehen →" : "leer", style: { fontFamily: "Georgia", fontSize: 11, fill: 0x7a6a4a, fontStyle: "italic" } });
    hint.position.set(82, 66);
    c.addChild(hint);

    // Info icons (desktop only to save space on mobile)
    const iconR = 9;
    const iconX = sw - 16;
    const iconY = sh - 16;
    const vsHash = VS_HASH_BY_ORGAN[kind];
    if (vsHash) {
      const linkBtn = new Container();
      const linkBg = new Graphics().circle(0, 0, iconR).fill({ color: 0xfdf6e3 }).stroke({ color: 0x3a2a1a, width: 1.5 });
      const linkTxt = new Text({ text: "?", style: { fontFamily: "Georgia", fontSize: 11, fill: 0x3a2a1a, fontWeight: "bold" } });
      linkTxt.anchor.set(0.5);
      linkBtn.addChild(linkBg, linkTxt);
      linkBtn.position.set(iconX - iconR * 2 - 6, iconY);
      linkBtn.eventMode = "static"; linkBtn.cursor = "pointer";
      linkBtn.on("pointertap", (e: FederatedPointerEvent) => {
        e.stopPropagation();
        window.open(VS_DEEPLINK_BASE + "#" + vsHash, "_blank", "noopener,noreferrer");
      });
      linkBtn.on("pointerdown", (e: FederatedPointerEvent) => e.stopPropagation());
      c.addChild(linkBtn);
    }
    const icon = new Container();
    const iconBg = new Graphics().circle(0, 0, iconR).fill({ color: 0x3a2a1a, alpha: 0.85 }).stroke({ color: 0xfdf6e3, width: 1.5 });
    const iTxt = new Text({ text: "i", style: { fontFamily: "Georgia", fontSize: 11, fill: 0xfdf6e3, fontWeight: "bold" } });
    iTxt.anchor.set(0.5);
    icon.addChild(iconBg, iTxt);
    icon.position.set(iconX, iconY);
    icon.eventMode = "static"; icon.cursor = "help";
    icon.on("pointertap", (e: FederatedPointerEvent) => {
      e.stopPropagation();
      if (infoPopup && infoPopupKind === kind) { closeInfoPopup(); }
      else { openInfoPopup(kind, e.global.x, e.global.y, "tap"); }
    });
    icon.on("pointerdown", (e: FederatedPointerEvent) => e.stopPropagation());
    c.addChild(icon);
  }

  const draggable = available && state.mode === "building";
  c.eventMode = "static";
  c.cursor = draggable ? "grab" : "help";
  if (draggable) {
    c.on("pointerdown", (e: FederatedPointerEvent) => startDragFromPalette(kind, e));
  }
  if (!layout.isMobile) {
    c.on("pointerenter", (e: FederatedPointerEvent) => { openInfoPopup(kind, e.global.x, e.global.y, "hover"); });
    c.on("pointerleave", () => { if (infoPopup && infoPopupKind === kind && infoPopupVia === "hover") closeInfoPopup(); });
  }
  return c;
}

// Goals panel
const goalsContainer = new Container();
uiLayer.addChild(goalsContainer);

const goalRows = new Map<ParticleType, Container>();
const goalPulseUntil = new Map<ParticleType, number>();
const GOAL_PULSE_MS = 420;

function drawGoals(): void {
  goalsContainer.removeChildren();
  goalRows.clear();
  if (!state.level) return;

  if (layout.isMobile) {
    // On mobile, goals are inline chips in the header — just refresh them
    drawGoalChipsMobile();
    return;
  }

  // Desktop: right panel
  const x0 = app.screen.width - layout.GOALS_W;
  const title = new Text({ text: "Ziele", style: { fontFamily: "Georgia", fontSize: 16, fill: 0x3a2a1a, fontWeight: "bold" } });
  title.position.set(x0 + 16, layout.HEADER_H + 12);
  goalsContainer.addChild(title);
  let y = layout.HEADER_H + 44;
  for (const goal of state.level.goals) {
    const have = state.progress.get(goal.type) ?? 0;
    const ratio = Math.min(1, have / goal.count);
    const row = new Container();
    row.position.set(x0 + 16, y);
    const bg = new Graphics().roundRect(0, 0, layout.GOALS_W - 32, 52, 8).fill({ color: 0xfdf6e3 }).stroke({ color: 0x3a2a1a, width: 2 });
    row.addChild(bg);
    const swatch = new Graphics().circle(16, 16, 10).fill({ color: PARTICLE_COLORS[goal.type] }).stroke({ color: 0x3a2a1a, width: 2 });
    const labelTxt = new Text({ text: `${PARTICLE_LABELS[goal.type]}`, style: { fontFamily: "Georgia", fontSize: 14, fill: 0x3a2a1a, fontWeight: "bold" } });
    labelTxt.position.set(34, 8);
    const countTxt = new Text({ text: `${have} / ${goal.count}`, style: { fontFamily: "Georgia", fontSize: 13, fill: 0x3a2a1a } });
    countTxt.position.set(layout.GOALS_W - 32 - 60, 8);
    const bar = new Graphics().roundRect(10, 32, layout.GOALS_W - 52, 12, 6).fill({ color: 0xeadcb4 }).stroke({ color: 0x3a2a1a, width: 1 });
    const barFill = new Graphics().roundRect(10, 32, Math.max(2, (layout.GOALS_W - 52) * ratio), 12, 6).fill({ color: PARTICLE_COLORS[goal.type] });
    row.addChild(bg, swatch, labelTxt, countTxt, bar, barFill);
    row.pivot.set((layout.GOALS_W - 32) / 2, 26);
    row.position.set(x0 + 16 + (layout.GOALS_W - 32) / 2, y + 26);
    goalsContainer.addChild(row);
    goalRows.set(goal.type, row);
    y += 64;
  }
  if (state.level.recipes && state.level.recipes.length > 0) {
    const recipeTitle = new Text({ text: "Rezept", style: { fontFamily: "Georgia", fontSize: 16, fill: 0x3a2a1a, fontWeight: "bold" } });
    recipeTitle.position.set(x0 + 16, y + 12);
    goalsContainer.addChild(recipeTitle);
    y += 40;
    for (const recipe of state.level.recipes) {
      const line = new Text({ text: recipe, style: { fontFamily: "Georgia", fontSize: 13, fill: 0x3a2a1a, wordWrap: true, wordWrapWidth: layout.GOALS_W - 32 } });
      line.position.set(x0 + 16, y);
      goalsContainer.addChild(line);
      y += Math.max(22, line.height + 4);
    }
    y += 12;
  }
  const legendTitle = new Text({ text: "Nahrung", style: { fontFamily: "Georgia", fontSize: 16, fill: 0x3a2a1a, fontWeight: "bold" } });
  legendTitle.position.set(x0 + 16, y + 12);
  goalsContainer.addChild(legendTitle);
  y += 44;
  for (const f of state.level.food) {
    const swatch = new Graphics().circle(x0 + 26, y + 10, 10).fill({ color: PARTICLE_COLORS[f.type] }).stroke({ color: 0x3a2a1a, width: 2 });
    const t = new Text({ text: `${PARTICLE_LABELS[f.type]} ×${f.count}`, style: { fontFamily: "Georgia", fontSize: 13, fill: 0x3a2a1a } });
    t.position.set(x0 + 46, y + 2);
    goalsContainer.addChild(swatch, t);
    y += 28;
  }
}

// --- Drag from palette ---

type DragState =
  | { kind: "palette"; organ: Organ; sourceKind: OrganKind }
  | { kind: "move"; organ: Organ; offset: { x: number; y: number } }
  | null;
let drag: DragState = null;

function startDragFromPalette(kind: OrganKind, e: FederatedPointerEvent): void {
  if (state.mode !== "building") return;
  const remaining = state.paletteStock.get(kind) ?? 0;
  if (kind !== "ramp" && remaining <= 0) return;
  const organ = new Organ(kind, e.global.x, e.global.y, layout.organScale);
  organLayer.addChild(organ.view);
  drag = { kind: "palette", organ, sourceKind: kind };
  setTrashState("armed");
}

function startDragOrgan(organ: Organ, e: FederatedPointerEvent): void {
  if (state.mode !== "building") return;
  drag = { kind: "move", organ, offset: { x: e.global.x - organ.view.x, y: e.global.y - organ.view.y } };
  setTrashState("armed");
}

app.stage.eventMode = "static";
app.stage.hitArea = app.screen;
app.stage.on("pointerdown", (e: FederatedPointerEvent) => {
  if (state.mode !== "building") return;
  // Ignore taps on UI panels
  const inPaletteLeft = !layout.isMobile && e.global.x < layout.PALETTE_W;
  const inGoalsRight = !layout.isMobile && e.global.x > app.screen.width - layout.GOALS_W;
  const inHeader = e.global.y < layout.HEADER_H;
  const inBottom = layout.isMobile && e.global.y > app.screen.height - layout.BOTTOM_H;
  if (inPaletteLeft || inGoalsRight || inHeader || inBottom) return;
  selectOrgan(null);
});
app.stage.on("pointermove", (e: FederatedPointerEvent) => {
  if (!drag) return;
  if (drag.kind === "palette") {
    drag.organ.setPosition(e.global.x, e.global.y);
  } else {
    drag.organ.setPosition(e.global.x - drag.offset.x, e.global.y - drag.offset.y);
  }
  setTrashState(isOverTrash(e.global.x, e.global.y) ? "hot" : "armed");
});

function endDrag(e: FederatedPointerEvent): void {
  if (!drag) return;
  if (isOverTrash(e.global.x, e.global.y)) {
    const { organ } = drag;
    if (drag.kind === "palette") { organLayer.removeChild(organ.view); }
    else { removeOrgan(organ); }
    drag = null; setTrashState("idle");
    return;
  }
  const { organ } = drag;
  const inside =
    e.global.x > workbench.x + 10 &&
    e.global.x < workbench.x + workbench.w - 10 &&
    e.global.y > workbench.y + 20 &&
    e.global.y < workbench.y + workbench.h - 20;

  if (drag.kind === "palette") {
    if (!inside) { organLayer.removeChild(organ.view); }
    else { commitOrganPlacement(organ, drag.sourceKind); }
  } else {
    if (!inside) { removeOrgan(organ); }
    else if (organ.body) { Matter.Body.setPosition(organ.body, { x: organ.view.x, y: organ.view.y }); }
  }
  drag = null; setTrashState("idle");
}
app.stage.on("pointerup", endDrag);
app.stage.on("pointerupoutside", endDrag);

function commitOrganPlacement(organ: Organ, sourceKind: OrganKind): void {
  state.organs.push(organ);
  Matter.World.add(phys.world, organ.createBody());
  if (sourceKind !== "ramp") {
    state.paletteStock.set(sourceKind, (state.paletteStock.get(sourceKind) ?? 0) - 1);
  }
  organ.view.eventMode = "static";
  organ.view.cursor = "grab";
  let pressedAt = 0;
  let pressedPos: { x: number; y: number } | null = null;
  organ.view.on("pointerdown", (e: FederatedPointerEvent) => {
    if (state.mode !== "building") return;
    pressedAt = performance.now();
    pressedPos = { x: e.global.x, y: e.global.y };
    selectOrgan(organ);
    e.stopPropagation();
    startDragOrgan(organ, e);
  });
  organ.view.on("pointerup", (e: FederatedPointerEvent) => {
    const dt = performance.now() - pressedAt;
    const dx = pressedPos ? e.global.x - pressedPos.x : 0;
    const dy = pressedPos ? e.global.y - pressedPos.y : 0;
    pressedPos = null;
    if (dt < 260 && Math.hypot(dx, dy) < 8) {
      rotateOrgan(organ, Math.PI / 12);
    }
  });
  attachRotationHandles(organ);
  selectOrgan(organ);
  drawPalette();
}

function rotateOrgan(organ: Organ, delta: number): void {
  organ.setRotation(organ.rotation + delta);
}

interface RotationHandles { container: Container; organ: Organ; }
const rotationHandles: RotationHandles[] = [];
let selectedOrgan: Organ | null = null;

function selectOrgan(organ: Organ | null): void { selectedOrgan = organ; }

function attachRotationHandles(organ: Organ): void {
  const container = new Container();
  container.alpha = 0.25;
  container.visible = false;
  const R = layout.HANDLE_R;
  // Spacing: positions are relative to the center. On mobile with R=26, spacing is wider.
  const spacing = R + 22;
  const left = makeRotateButton("↺", () => rotateOrgan(organ, -Math.PI / 12));
  const right = makeRotateButton("↻", () => rotateOrgan(organ, Math.PI / 12));
  const remove = makeRotateButton("✕", () => removeOrgan(organ), 0xc04030);
  const info = makeRotateButton("i", () => {
    const pos = organ.view.getGlobalPosition();
    openInfoPopup(organ.kind, pos.x, pos.y, "tap");
  });
  left.position.set(-spacing * 3 + R, 0);
  right.position.set(-spacing + R, 0);
  remove.position.set(spacing - R, 0);
  info.position.set(spacing * 3 - R, 0);
  container.addChild(left, right, remove, info);
  organLayer.addChild(container);
  rotationHandles.push({ container, organ });
}

function makeRotateButton(glyph: string, onTap: () => void, fillColor = 0xfdf6e3): Container {
  const c = new Container();
  const R = layout.HANDLE_R;
  const bg = new Graphics()
    .circle(R, 0, R)
    .fill({ color: fillColor })
    .stroke({ color: 0x3a2a1a, width: 2 });
  const t = new Text({
    text: glyph,
    style: {
      fontFamily: "Georgia",
      fontSize: layout.isMobile ? 18 : 20,
      fill: fillColor === 0xc04030 ? 0xfdf6e3 : 0x3a2a1a,
      fontWeight: "bold",
    },
  });
  t.anchor.set(0.5);
  t.position.set(R, 0);
  c.addChild(bg, t);
  c.eventMode = "static";
  c.cursor = "pointer";
  // Expanded hit area for touch
  const hit = new Graphics().circle(R, 0, R + 8).fill({ color: 0xffffff, alpha: 0.001 });
  c.addChildAt(hit, 0);
  c.on("pointerdown", (e: FederatedPointerEvent) => e.stopPropagation());
  c.on("pointertap", onTap);
  return c;
}

function updateRotationHandles(): void {
  const buildingMode = state.mode === "building";
  for (const h of rotationHandles) {
    const show = buildingMode && h.organ === selectedOrgan;
    h.container.visible = show;
    if (!show) continue;
    const geom = h.organ.geom;
    const offsetY = -(geom.height * 0.5 + layout.HANDLE_R + 10);
    h.container.position.set(h.organ.view.x, h.organ.view.y + offsetY);
  }
}

function removeOrgan(organ: Organ): void {
  if (selectedOrgan === organ) selectedOrgan = null;
  organLayer.removeChild(organ.view);
  if (organ.body) Matter.World.remove(phys.world, organ.body);
  state.organs = state.organs.filter((o) => o !== organ);
  for (let i = rotationHandles.length - 1; i >= 0; i--) {
    if (rotationHandles[i].organ === organ) {
      organLayer.removeChild(rotationHandles[i].container);
      rotationHandles.splice(i, 1);
    }
  }
  if (organ.kind !== "ramp") {
    const prev = state.paletteStock.get(organ.kind) ?? 0;
    state.paletteStock.set(organ.kind, prev + 1);
  }
  drawPalette();
}

// --- Food spawners ---

interface Spawner { x: number; y: number; type: ParticleType; }
let spawners: Spawner[] = [];
function drawSpawners(): void {
  const hopperLayer = new Graphics();
  bgLayer.addChild(hopperLayer);
  for (const s of spawners) {
    hopperLayer
      .moveTo(s.x - 22, s.y - 24).lineTo(s.x + 22, s.y - 24)
      .lineTo(s.x + 10, s.y).lineTo(s.x - 10, s.y)
      .closePath().fill({ color: 0xcaa46a }).stroke({ color: 0x3a2a1a, width: 2 });
    hopperLayer.circle(s.x, s.y - 34, 7).fill({ color: PARTICLE_COLORS[s.type] }).stroke({ color: 0x3a2a1a, width: 2 });
  }
}

function buildSpawners(level: Level): void {
  spawners = [];
  const count = level.food.length;
  const startX = workbench.x + workbench.w * 0.2;
  const endX = workbench.x + workbench.w * 0.8;
  const step = count > 1 ? (endX - startX) / (count - 1) : 0;
  for (let i = 0; i < level.food.length; i++) {
    spawners.push({
      x: count > 1 ? startX + step * i : workbench.x + workbench.w / 2,
      y: workbench.y + 50,
      type: level.food[i].type,
    });
  }
}

function buildSpawnQueue(level: Level): ParticleType[] {
  const q: ParticleType[] = [];
  for (const f of level.food) for (let i = 0; i < f.count; i++) q.push(f.type);
  q.sort(() => Math.random() - 0.5);
  return q;
}

function spawnNextParticle(): void {
  const type = state.spawnQueue.shift();
  if (!type) return;
  const spawner = spawners.find((s) => s.type === type) ?? spawners[0];
  const p = new Particle(type, spawner.x, spawner.y, layout.particleR);
  Matter.World.add(phys.world, p.body);
  particleLayer.addChild(p.view);
  state.particles.push(p);
}

// --- Collision handling ---

Matter.Events.on(phys.engine, "collisionStart", (evt) => {
  for (const pair of evt.pairs) {
    const a = pair.bodyA as Matter.Body & { particle?: Particle; organ?: Organ };
    const b = pair.bodyB as Matter.Body & { particle?: Particle; organ?: Organ };
    const particle = a.particle ?? b.particle;
    const organ = a.organ ?? b.organ;
    if (!particle || !organ || particle.absorbed) continue;
    if (particle.ghost) continue;
    if (organ.kind === "darm") { absorbParticle(particle); organ.flash(performance.now()); continue; }
    if (organ.kind === "speiseroehre") { organ.flash(performance.now(), 120); continue; }
    const next = transform(organ.kind, particle.type);
    if (next) { particle.setType(next); organ.flash(performance.now()); }
  }
});

Matter.Events.on(phys.engine, "collisionActive", (evt) => {
  for (const pair of evt.pairs) {
    const a = pair.bodyA as Matter.Body & { particle?: Particle; organ?: Organ };
    const b = pair.bodyB as Matter.Body & { particle?: Particle; organ?: Organ };
    const particle = a.particle ?? b.particle;
    const organ = a.organ ?? b.organ;
    if (!particle || !organ || particle.absorbed) continue;
    if (organ.kind !== "speiseroehre") continue;
    const angle = organ.body?.angle ?? 0;
    const speedPerStep = SPEISEROEHRE_SPEED / 60;
    Matter.Body.setVelocity(particle.body, { x: Math.cos(angle) * speedPerStep, y: Math.sin(angle) * speedPerStep });
  }
});

function absorbParticle(p: Particle): void {
  p.absorbed = true;
  if (state.level?.goals.some((g) => g.type === p.type)) {
    state.progress.set(p.type, (state.progress.get(p.type) ?? 0) + 1);
    drawGoals();
    goalPulseUntil.set(p.type, performance.now() + GOAL_PULSE_MS);
    checkWin();
  }
  Matter.World.remove(phys.world, p.body);
  particleLayer.removeChild(p.view);
  state.particles = state.particles.filter((x) => x !== p);
}

function checkWin(): void {
  if (!state.level) return;
  const won = state.level.goals.every((g) => (state.progress.get(g.type) ?? 0) >= g.count);
  if (won) { state.mode = "won"; showFeedback(true); drawHeader(); }
}

function showFeedback(success: boolean): void {
  if (feedbackOverlay) uiLayer.removeChild(feedbackOverlay);
  const c = new Container();
  const bg = new Graphics().rect(0, 0, app.screen.width, app.screen.height).fill({ color: 0x000000, alpha: 0.35 });
  const panelW = Math.min(520, app.screen.width - 32);
  const panelH = success ? 240 : 200;
  const panelX = app.screen.width / 2 - panelW / 2;
  const panelY = app.screen.height / 2 - panelH / 2;
  const panel = new Graphics()
    .roundRect(panelX, panelY, panelW, panelH, 16)
    .fill({ color: 0xfdf6e3 }).stroke({ color: 0x3a2a1a, width: 3 });
  const headFontSize = layout.isMobile ? 17 : 22;
  const head = new Text({
    text: success ? "Grossartig! Verdauung geglückt 🎉" : "Fast! Probier eine andere Anordnung.",
    style: { fontFamily: "Georgia", fontSize: headFontSize, fill: 0x3a2a1a, fontWeight: "bold", align: "center", wordWrap: true, wordWrapWidth: panelW - 32 },
  });
  head.anchor.set(0.5);
  head.position.set(app.screen.width / 2, panelY + 50);
  const sub = new Text({
    text: success ? "Alle Nährstoffe sind im Darm angekommen." : "Jedes Tüfteln bringt dich näher an die Lösung.",
    style: { fontFamily: "Georgia", fontSize: 14, fill: 0x3a2a1a, align: "center" },
  });
  sub.anchor.set(0.5);
  sub.position.set(app.screen.width / 2, panelY + (layout.isMobile ? 85 : 90));
  c.addChild(bg, panel, head, sub);

  const dismiss = (): void => { if (feedbackOverlay === c) { uiLayer.removeChild(c); feedbackOverlay = null; } };
  const replayW = layout.isMobile ? 120 : 170;
  const nextW = layout.isMobile ? 160 : 220;
  const replayBtn = makeButton("↻ Replay", replayW, 44, () => { dismiss(); onStart(); });
  const nextBtnInline = makeButton("▶| Nächstes Level", nextW, 44, () => { dismiss(); onNextLevel(); });
  const gap = 12;
  const totalW = replayW + gap + nextW;
  const baseX = app.screen.width / 2 - totalW / 2;
  const btnY = panelY + panelH - 60;
  replayBtn.position.set(baseX, btnY);
  nextBtnInline.position.set(baseX + replayW + gap, btnY);
  c.addChild(replayBtn, nextBtnInline);
  uiLayer.addChild(c);
  feedbackOverlay = c;
}

// --- Level lifecycle ---

async function loadAndStart(path: string): Promise<void> {
  const level = await loadLevel(path);
  state.level = level;
  state.mode = "building";
  state.progress.clear();
  state.paletteStock.clear();
  for (const item of level.palette) {
    state.paletteStock.set(item.organ, (state.paletteStock.get(item.organ) ?? 0) + item.count);
  }
  for (const o of state.organs) if (o.body) Matter.World.remove(phys.world, o.body);
  organLayer.removeChildren();
  state.organs = [];
  rotationHandles.length = 0;
  selectedOrgan = null;
  for (const p of state.particles) Matter.World.remove(phys.world, p.body);
  clearGhosts(state.ghosts, phys, particleLayer);
  particleLayer.removeChildren();
  state.particles = [];
  state.spawnQueue = [];

  buildSpawners(level);
  state.ghosts = createGhostState(spawners.length);
  rebuildBoundary();
  drawBackground();
  drawSpawners();
  titleText.text = level.title;
  if (hintText) hintText.text = level.hint;
  drawPalette();
  drawGoals();
  drawHeader();

  if (level.prePlaced) {
    for (const pp of level.prePlaced) {
      const x = workbench.x + workbench.w * pp.rx;
      const y = workbench.y + workbench.h * pp.ry;
      const organ = new Organ(pp.organ, x, y, layout.organScale);
      if (pp.rotation) organ.setRotation(pp.rotation);
      organLayer.addChild(organ.view);
      commitOrganPlacement(organ, pp.organ);
    }
    selectOrgan(null);
  }

  if (level.tutorial && level.tutorial.length > 0) {
    showTutorialBubbles(level.id, level.tutorial);
  } else {
    closeTutorialBubble();
  }
}

function onToggleRun(): void {
  if (state.mode === "running") { onReset(); return; }
  if (state.mode === "won") { onReset(); }
  onStart();
}

function onStart(): void {
  if (state.mode !== "building" || !state.level) return;
  clearGhosts(state.ghosts, phys, particleLayer);
  state.mode = "running";
  state.spawnQueue = buildSpawnQueue(state.level);
  state.spawnTimer = 0;
  drawHeader();
}

function onReset(): void {
  if (!state.level) return;
  for (const p of state.particles) Matter.World.remove(phys.world, p.body);
  clearGhosts(state.ghosts, phys, particleLayer);
  particleLayer.removeChildren();
  state.particles = [];
  state.spawnQueue = [];
  state.spawnTimer = 0;
  state.progress.clear();
  state.mode = "building";
  if (feedbackOverlay) { uiLayer.removeChild(feedbackOverlay); feedbackOverlay = null; }
  drawGoals();
  drawHeader();
}

function onNextLevel(): void {
  state.levelIdx = (state.levelIdx + 1) % state.levels.length;
  loadAndStart(state.levels[state.levelIdx]);
}

// --- Main ticker ---

let lastTick = performance.now();
let settledMs = 0;
app.ticker.add(() => {
  const now = performance.now();
  const dt = now - lastTick;
  lastTick = now;

  if (state.mode === "running") {
    state.spawnTimer += dt;
    if (state.spawnTimer > 380 && state.spawnQueue.length > 0) {
      state.spawnTimer = 0;
      spawnNextParticle();
    }
    if (state.spawnQueue.length === 0) {
      const allSlow = state.particles.every((p) => { const v = p.body.velocity; return Math.hypot(v.x, v.y) < 0.4; });
      if (allSlow) settledMs += dt;
      else settledMs = 0;
      if (state.particles.length === 0 || settledMs > 1200) { finishRun(); }
    } else { settledMs = 0; }
    phys.step(dt);
  }

  if (state.mode === "building") {
    tickGhosts(dt, state.ghosts, spawners, phys, particleLayer, now, workbench.y + workbench.h, layout.particleR);
    phys.step(dt);
  }

  for (const o of state.organs) o.tick(now);
  for (const p of state.particles) p.sync();
  updateRotationHandles();
  applyShake(now);
  updateGoalPulses(now);
  for (const p of [...state.particles]) {
    if (p.body.position.y > app.screen.height + 80) {
      Matter.World.remove(phys.world, p.body);
      particleLayer.removeChild(p.view);
      state.particles = state.particles.filter((x) => x !== p);
    }
  }
});

function finishRun(): void {
  if (!state.level || state.mode !== "running") return;
  settledMs = 0;
  const won = state.level.goals.every((g) => (state.progress.get(g.type) ?? 0) >= g.count);
  if (won) { state.mode = "won"; showFeedback(true); }
  else { showFeedback(false); state.mode = "building"; }
  for (const p of [...state.particles]) { Matter.World.remove(phys.world, p.body); particleLayer.removeChild(p.view); }
  state.particles = [];
  clearGhosts(state.ghosts, phys, particleLayer);
  drawPalette();
  drawHeader();
}

// Deferred resize handler — handles both window resize and orientation change.
// Defers to the next animation frame so Pixi's resizeTo:window plugin and iOS
// Safari have time to settle the new viewport dimensions before we recompute.
let resizeRAF: ReturnType<typeof requestAnimationFrame> | null = null;
function handleResize(): void {
  if (resizeRAF !== null) cancelAnimationFrame(resizeRAF);
  resizeRAF = requestAnimationFrame(() => {
    resizeRAF = null;
    if (!state.level) return;
    const wasLayout = layout;
    layout = computeLayout();
    const layoutChanged = wasLayout.isMobile !== layout.isMobile;

    // Rebuild physics walls to match the new screen size
    rebuildOuterWalls();
    rebuildBoundary();

    // Rebuild spawner positions for the new workbench area
    buildSpawners(state.level!);

    drawBackground();
    drawSpawners();
    drawHeader();
    drawPalette();
    drawGoals();

    if (layoutChanged) {
      // Rotation handles were built with old handle radius — rebuild them
      for (const h of rotationHandles) {
        organLayer.removeChild(h.container);
        h.container.destroy({ children: true });
      }
      rotationHandles.length = 0;
      for (const organ of state.organs) attachRotationHandles(organ);
    }
  });
}
window.addEventListener("resize", handleResize);
window.addEventListener("orientationchange", handleResize);

// Boot
drawBackground();
drawHeader();
await loadAndStart(state.levels[0]);
